import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ROLE_LABELS } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["super_admin", "operations", "accounts", "label_manager", "artist", "label"];

export default function Users() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [artists, setArtists] = useState([]);
  const [labels, setLabels] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "Admin@123", role: "artist", artist_id: "", label_id: "" });

  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/artists").then((r) => setArtists(r.data));
    api.get("/labels").then((r) => setLabels(r.data));
  }, []);

  const decide = async (uid, action) => {
    await api.post(`/users/${uid}/${action}`);
    toast.success(action === "approve" ? "User approved" : "User rejected");
    load();
  };

  const create = async () => {
    if (!f.name || !f.email) return toast.error("Name and email required");
    try {
      await api.post("/users", { ...f, artist_id: f.artist_id || null, label_id: f.label_id || null });
      toast.success("User created");
      setOpen(false);
      setF({ name: "", email: "", password: "Admin@123", role: "artist", artist_id: "", label_id: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-bold tracking-tight">Users</h1><p className="mt-1 text-muted-foreground">Create users and assign roles.</p></div>
        <Button onClick={() => setOpen(true)} data-testid="new-user-btn"><Plus className="mr-2 h-4 w-4" />Create User</Button>
      </div>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="cursor-pointer" onClick={() => nav(`/users/${u.id}`)}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">{ROLE_LABELS[u.role]}</Badge></TableCell>
                <TableCell>
                  {u.status === "pending" ? <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-400">Pending</Badge>
                    : u.status === "rejected" ? <Badge variant="outline" className="border-rose-500/30 bg-rose-500/10 text-rose-400">Rejected</Badge>
                    : u.status === "suspended" ? <Badge variant="outline" className="border-orange-500/30 bg-orange-500/10 text-orange-400">Suspended</Badge>
                    : u.status === "terminated" ? <Badge variant="outline" className="border-rose-500/30 bg-rose-500/10 text-rose-400">Terminated</Badge>
                    : <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">Approved</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {u.status === "pending" || u.status === "rejected" ? (
                    <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" onClick={() => decide(u.id, "approve")} data-testid={`approve-user-${u.id}`}>Approve</Button>
                      {u.status === "pending" && <Button size="sm" variant="destructive" onClick={() => decide(u.id, "reject")} data-testid={`reject-user-${u.id}`}>Reject</Button>}
                    </div>
                  ) : <span className="text-xs text-muted-foreground">View →</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" className="mt-1" /></div>
            <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" className="mt-1" /></div>
            <div><Label>Password</Label><Input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" className="mt-1" /></div>
            <div>
              <Label>Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {f.role === "artist" && (
              <div><Label>Link Artist</Label>
                <Select value={f.artist_id} onValueChange={(v) => setF({ ...f, artist_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="user-artist"><SelectValue placeholder="Select artist" /></SelectTrigger>
                  <SelectContent>{artists.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {(f.role === "label" || f.role === "label_manager") && (
              <div><Label>Link Label</Label>
                <Select value={f.label_id} onValueChange={(v) => setF({ ...f, label_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="user-label"><SelectValue placeholder="Select label" /></SelectTrigger>
                  <SelectContent>{labels.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-user-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
